'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { useConcur } from '../../hooks/use-concur';
import { GlobalRegion } from '../../shared/constants';
import { concurConnectStoreSelector, userStoreSelector } from '../../store';

import { COMPANY_DECLINED_URL_PARAMETER } from './constants';
import { getOneYearFromNow } from './utils';

export const useConcurConnectDialog = () => {
  const router = useRouter();
  const pathname = usePathname();
  const { navigationLinks, region } = usePageContext();
  const {
    linkConcurProfile,
    hasCorporateDiscount,
    corporateDiscountData,
    deleteCorporateCodes,
  } = useConcur();
  const userProfile = userStoreSelector.use.userProfile();
  const userSession = userStoreSelector.use.userSession();
  const expiryDate = concurConnectStoreSelector.use.expiryDate();
  const sessionIdCached = concurConnectStoreSelector.use.sessionId?.();
  const setSessionIdConcur = concurConnectStoreSelector.use.setSessionId();
  const setExpiryDate = concurConnectStoreSelector.use.setExpiryDate();
  const setView = concurConnectStoreSelector.use.setView();
  const view = concurConnectStoreSelector.use.view?.();
  const setHasAgreed = concurConnectStoreSelector.use.setHasAgreed();
  const [companyName, setCompanyName] = useState<string>('');
  const isCodeRemovingProcess = useRef(false);
  const isHomePage = pathname === navigationLinks.home;
  const sessionId = userSession?.id;
  const isNamRegion = region === GlobalRegion.NAM;

  const saveConnectState = useCallback(
    (hasAgreed: boolean) => {
      setExpiryDate(getOneYearFromNow());
      setHasAgreed(hasAgreed);
    },
    [setExpiryDate, setHasAgreed],
  );

  /**
   * Check Concur once in a session.
   */
  useEffect(() => {
    if (hasCorporateDiscount === false) {
      setSessionIdConcur(sessionId ?? '');
    }
  }, [hasCorporateDiscount, sessionId, setSessionIdConcur]);

  /**
   * If the user declines their association with the company,
   * all corporate codes should be replaced with a special one and the declined dialog screen should be displayed.
   */
  useEffect(() => {
    if (
      location.search.includes(COMPANY_DECLINED_URL_PARAMETER) &&
      !isCodeRemovingProcess.current &&
      userProfile?.profileNumber
    ) {
      isCodeRemovingProcess.current = true;

      void deleteCorporateCodes()
        .then((response) => {
          if (response) {
            setView({ isDeclined: true });
          }

          return response;
        })
        .finally(() => {
          isCodeRemovingProcess.current = false;
        });
    }
  }, [
    deleteCorporateCodes,
    navigationLinks.accountDashboard,
    router,
    setView,
    userProfile?.profileNumber,
  ]);

  /**
   * On the homepage, if the user is authenticated and has not seen the connect dialog in a year:
   * - If they have a corporate discount code, show the start dialog screen.
   * - If they do not have a corporate discount code, request the Concur status (and possibly retrieve the code).
   */
  useEffect(() => {
    const isExpiryDatePassed = Date.now() >= expiryDate;

    if (isNamRegion && userProfile && isExpiryDatePassed && isHomePage) {
      if (corporateDiscountData) {
        setCompanyName(corporateDiscountData.companyName || 'a company');
        setView({ isStart: true });
      } else {
        if (sessionId === sessionIdCached) return;
        void linkConcurProfile({ wizardNumber: userProfile.profileNumber });
      }
    }
  }, [
    isNamRegion,
    isHomePage,
    linkConcurProfile,
    expiryDate,
    sessionId,
    sessionIdCached,
    userProfile,
    corporateDiscountData,
    setView,
  ]);

  return {
    companyName,
    setView,
    view,
    saveConnectState,
    isHomePage,
  };
};
